import { Trash2 } from 'lucide-react';
import * as React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { AAllowDenyList, deleteAllowDenyList } from '../../../atoms/allowDenyList';
import { AUsers } from '../../../atoms/global/users';
import { AGroups } from '../../../atoms/groups';
import { Column, Table } from '../../../components_v2/table/Table';
import { DarkGreySidely, RedSidely } from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import { DriveRestriction, getDriveRestrictions } from '../../documents/actions';
import { ToolbarState } from '../../globals/mainPage/mainPage';
import { LoadingStateEnum } from '../../import/model';
import { ComponentLoader } from '../../map/modalRight/ModalCalendar';
import { TableRow } from '../../orders/templateOrders/style/Style';

// Audit list of the drive path restrictions created from the file manager's
// context menu: every deny entry with its restricted path, removable here.
export default function DriveRestrictionsSettings(props: { setToolBarState: (value: ToolbarState) => void }) {
	const users = useRecoilValue(AUsers);
	const groups = useRecoilValue(AGroups);
	const [_, setAtom] = useRecoilState(AAllowDenyList);
	const [restrictions, setRestrictions] = React.useState<DriveRestriction[]>([]);
	const [loadingState, setLoadingState] = React.useState(LoadingStateEnum.LOADING);

	const refresh = React.useCallback(() => {
		setLoadingState(LoadingStateEnum.LOADING);
		getDriveRestrictions().then(res => {
			setRestrictions(res);
			setLoadingState(LoadingStateEnum.LOADED);
		}).catch(() => setLoadingState(LoadingStateEnum.ERROR));
	}, []);

	React.useEffect(() => {
		props.setToolBarState({});
		refresh();
	}, []);

	const columns: Column<DriveRestriction>[] = React.useMemo(() => [
		{
			id: 'path',
			Header: translateToString('file_manager.restricted_path'),
			accessor: row => <TableRow fontWeight='500' color={DarkGreySidely}>{row.path}</TableRow>,
			width: undefined,
			toolTip: row => row.path,
		},
		{
			id: 'denied',
			Header: translateToString('allow_deny_list.restrictions'),
			accessor: row => {
				const names = [
					...row.users.map(id => users.find(u => u.id === id)?.name),
					...row.groups.map(id => groups.find(g => g.id === id)?.name),
				].filter((name): name is string => !!name);
				return <TableRow>{names.join(', ')}</TableRow>;
			},
			width: undefined,
		},
		{
			id: 'actions',
			Header: <></>,
			accessor: row => <TableRow cursor='pointer' color={RedSidely} onClick={() => {
				setLoadingState(LoadingStateEnum.LOADING);
				deleteAllowDenyList(row.id, setAtom).then(refresh);
			}}>
				<Trash2 size={18} color={RedSidely} />
			</TableRow>,
			width: 60,
			disableSortBy: true,
			unresizeable: true,
		},
	], [users, groups, refresh]);

	return <>
		<Table
			height='calc(100vh - 200px)'
			columns={columns}
			data={restrictions}
		/>
		<ComponentLoader loadingState={loadingState} allScreen />
	</>;
}
